import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react'
import { useAuth } from './AuthProvider'
import { api, invalidateCache } from '../lib/api'

const UserRoleContext = createContext({
  roles: [],
  roleDetails: [],
  hasRole: () => false,
  isParishAdmin: false,
  isSuperAdmin: false,
  isDonor: false,
  loading: true,
  hasFetched: false,
  getParishAdminParishId: () => null,
})

export const useUserRole = () => useContext(UserRoleContext)

export const UserRoleProvider = ({ children }) => {
  const { user } = useAuth()
  const [roles, setRoles] = useState([])
  const [roleDetails, setRoleDetails] = useState([])
  const [loading, setLoading] = useState(true)
  const [hasFetched, setHasFetched] = useState(false)
  const hasFetchedRef = useRef(false)
  const userIdRef = useRef(null)
  const fetchingRef = useRef(false)

  useEffect(() => {
    if (!user) {
      setRoles([])
      setRoleDetails([])
      setLoading(false)
      setHasFetched(false)
      hasFetchedRef.current = false
      userIdRef.current = null
      fetchingRef.current = false
      return
    }

    // Only fetch if user changed or we haven't fetched yet
    if (hasFetchedRef.current && userIdRef.current === user.id) {
      setLoading(false)
      setHasFetched(true)
      return
    }

    // If the user identity changed, clear stale cached responses so the
    // new user doesn't inherit a previous session's role data.
    if (userIdRef.current && userIdRef.current !== user.id) {
      invalidateCache('/api/v1/auth/me', '/api/v1/users')
    }

    // Prevent concurrent fetches
    if (fetchingRef.current) {
      return
    }

    const fetchRoles = async () => {
      try {
        fetchingRef.current = true
        setLoading(true)
        // Use /auth/me endpoint which includes roles in the response
        const userData = await api.getMe()
        
        // Extract roles and roleDetails from the response
        // According to API contract, /auth/me returns: { roles: ["user"], roleDetails: [...] }
        const roleNames = userData?.roles || []
        const details = userData?.roleDetails || []
        
        // Set roles and roleDetails, then set loading to false
        setRoles(roleNames)
        setRoleDetails(details)
        hasFetchedRef.current = true
        userIdRef.current = user.id
        setHasFetched(true)
        setLoading(false)
        fetchingRef.current = false
      } catch (error) {
        console.error('Error fetching user roles:', error)
        setRoles([])
        setRoleDetails([])
        setHasFetched(true) // Mark as fetched even on error, so we don't wait indefinitely
        setLoading(false)
        fetchingRef.current = false
      }
    }

    fetchRoles()
  }, [user])

  const hasRole = (roleName) => roles.includes(roleName)
  
  // Use useMemo to ensure role checks are always computed from current roles
  const isParishAdmin = useMemo(() => hasRole('parish-admin'), [roles])
  const isSuperAdmin = useMemo(() => hasRole('super-admin'), [roles])
  const isDonor = useMemo(() => hasRole('user') || (!isParishAdmin && !isSuperAdmin && user), [roles, isParishAdmin, isSuperAdmin, user])
  
  // Get parish ID for parish-admin role
  const getParishAdminParishId = () => {
    const parishAdminRole = roleDetails.find(r => r.name === 'parish-admin')
    return parishAdminRole?.parishId || null
  }

  return (
    <UserRoleContext.Provider
      value={{
        roles,
        roleDetails,
        hasRole,
        isParishAdmin,
        isSuperAdmin,
        isDonor,
        loading,
        hasFetched,
        getParishAdminParishId,
      }}
    >
      {children}
    </UserRoleContext.Provider>
  )
}
